import { Box, Typography, Button, Card, CardContent, Chip } from "@mui/material";
import Header from "../components/header";
import { Link } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CodeIcon from "@mui/icons-material/Code";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ExtensionIcon from "@mui/icons-material/Extension";

const WhyTypeTwice = () => {
  const features = [
    {
      icon: <AutoFixHighIcon />,
      title: "Smart Autocomplete",
      description: "Type const [name, and it completes the setter and useState() for you.",
    },
    {
      icon: <CodeIcon />,
      title: "Import-Aware",
      description: "Detects your existing React import style and matches it — or adds one if missing.",
    },
    {
      icon: <ExtensionIcon />,
      title: "Zero Config",
      description: "Install and go. Works immediately across JS, TS, JSX, and TSX files.",
    },
  ];

  const supported = [
    "JavaScript (.js)",
    "TypeScript (.ts)",
    "JSX (.jsx)",
    "TSX (.tsx)",
    "Named imports: import { useState } from 'react'",
    "Default imports: import React from 'react'",
    "Auto-adds import if none exists",
    "Cursor positioned for initial state value",
  ];

  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
      <Header />

      <Box sx={{ px: 3, pt: 3, maxWidth: "1200px", margin: "0 auto" }}>
        <Typography
          component={Link}
          to="/projects"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            color: "#10b981",
            textDecoration: "none",
            fontFamily: "Roboto",
            fontWeight: 500,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: "1rem" }} />
          Projects
        </Typography>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
          px: 3,
        }}
>
        <Box
          sx={{
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            gap: 6,
          }}
        >
          <Box sx={{ flex: 1, textAlign: { xs: "center", md: "left" } }}>
            <Box
              sx={{
                display: "inline-flex",
                p: 2,
                borderRadius: 3,
                backgroundColor: "rgba(255,255,255,0.15)",
                mb: 3,
                margin: { xs: "0 auto 24px", md: "0 0 24px 0" },
              }}
            >
              <ExtensionIcon sx={{ fontSize: 80, color: "white" }} />
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontFamily: "Roboto Slab",
                fontWeight: 700,
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                mb: 2,
              }}
            >
              WhyTypeTwice
            </Typography>

            <Typography
              variant="h5"
              sx={{ fontFamily: "Roboto", fontSize: { xs: "1.25rem", md: "1.5rem" }, mb: 1, opacity: 0.95 }}
            >
              useState, done in one.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontFamily: "Roboto", fontSize: { xs: "1rem", md: "1.1rem" }, mb: 4, opacity: 0.9 }}
            >
              A VS Code extension that auto-completes React useState destructuring. Start typing{" "}
              <Box component="span" sx={{ fontFamily: "monospace", backgroundColor: "rgba(255,255,255,0.15)", px: 0.75, py: 0.25, borderRadius: 1 }}>
                const [name,
              </Box>{" "}
              and let it do the rest.
            </Typography>

            <Box sx={{ display: "flex", gap: 2, justifyContent: { xs: "center", md: "flex-start" }, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                href="https://github.com/brandmch/WhyTypeTwice"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: "white",
                  color: "#10b981",
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                View on GitHub
              </Button>
              <Chip
                label="Free • Open Source • MIT License"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 500,
                  alignSelf: "center",
                }}
              />
            </Box>
          </Box>

          {/* Code Demo */}
          <Box sx={{ flex: 1, width: "100%" }}>
            <Card
              sx={{
                borderRadius: 3,
                backgroundColor: "#1e1e2e",
                boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
                border: "2px solid rgba(255,255,255,0.1)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 2, py: 1, backgroundColor: "#2a2a3d", display: "flex", gap: 1 }}>
                {["#ff5f57", "#febc2e", "#28c840"].map((color) => (
                  <Box key={color} sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: color }} />
                ))}
                <Typography variant="caption" sx={{ color: "#666", ml: 1, alignSelf: "center" }}>
                  App.jsx
                </Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: { xs: "0.8rem", md: "0.95rem" },
                    color: "#cdd6f4",
                    m: 0,
                    lineHeight: 1.8,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  <Box component="span" sx={{ color: "#6c7086" }}>{"// You type:"}{"\n"}</Box>
                  <Box component="span" sx={{ color: "#cba6f7" }}>const </Box>
                  <Box component="span" sx={{ color: "#cdd6f4" }}>[count,{"\n\n"}</Box>
                  <Box component="span" sx={{ color: "#6c7086" }}>{"// Extension completes:"}{"\n"}</Box>
                  <Box component="span" sx={{ color: "#cba6f7" }}>const </Box>
                  <Box component="span" sx={{ color: "#cdd6f4" }}>[count, setCount] = </Box>
                  <Box component="span" sx={{ color: "#89b4fa" }}>useState</Box>
                  <Box component="span" sx={{ color: "#cdd6f4" }}>(</Box>
                  <Box component="span" sx={{ color: "#a6e3a1" }}>▌</Box>
                  <Box component="span" sx={{ color: "#cdd6f4" }}>)</Box>
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "white" }}>
        <Box sx={{ maxWidth: "1200px", margin: "0 auto" }}>
          <Typography
            variant="h3"
            sx={{ fontFamily: "Roboto Slab", textAlign: "center", mb: 2, fontWeight: 700 }}
          >
            Stop Typing the Obvious
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "#666", mb: 6, fontSize: "1.1rem" }}
          >
            Every useState you write is the same pattern. So why type it twice?
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 4,
            }}
          >
            {features.map((feature, index) => (
              <Card
                key={index}
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                }}
              >
                <CardContent sx={{ textAlign: "center", p: 4 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      p: 2,
                      borderRadius: "50%",
                      backgroundColor: "#ecfdf5",
                      color: "#10b981",
                      fontSize: "2.5rem",
                      mb: 2,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontFamily: "Roboto Slab", fontWeight: 600, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Supported Section */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "#f5f7fa" }}>
        <Box sx={{ maxWidth: "800px", margin: "0 auto" }}>
          <Typography
            variant="h3"
            sx={{ fontFamily: "Roboto Slab", textAlign: "center", mb: 2, fontWeight: 700 }}
          >
            Works Everywhere You Work
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: "center", color: "#666", mb: 6, fontSize: "1.1rem" }}
          >
            Supports every React file type and adapts to your import style.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 2,
            }}
          >
            {supported.map((item, index) => (
              <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <CheckCircleIcon sx={{ color: "#10b981", fontSize: "1.4rem", flexShrink: 0 }} />
                <Typography variant="body1" sx={{ fontFamily: "Roboto" }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Footer CTA */}
      <Box sx={{ py: 10, px: 3, backgroundColor: "white", textAlign: "center" }}>
        <Box sx={{ maxWidth: "800px", margin: "0 auto" }}>
          <Typography variant="h3" sx={{ fontFamily: "Roboto Slab", fontWeight: 700, mb: 3 }}>
            Install It in 30 Seconds
          </Typography>
          <Typography variant="body1" sx={{ color: "#666", mb: 4, fontSize: "1.1rem" }}>
            Clone the repo, run npm install, and launch from VS Code. That's it.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="https://github.com/brandmch/WhyTypeTwice"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: "#10b981",
              color: "white",
              fontWeight: 600,
              px: 5,
              py: 2,
              fontSize: "1.1rem",
              mb: 3,
              "&:hover": {
                backgroundColor: "#047857",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 16px rgba(16,185,129,0.3)",
              },
              transition: "all 0.3s ease",
            }}
          >
            View on GitHub
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default WhyTypeTwice;
